// src/components/pages/ChallengeDetailPage.js

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { auth } from "../../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import {
  fetchChallenge,
  fetchComments,
  addComment,
  fetchSubmissions,
} from "../../services/challengeService";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import InfoSection from "../molecules/InfoSection";
import { useTheme } from "styled-components";
import { useAuth } from "../../context/AuthContext";
import SubmissionsSection from "../organisms/SubmissionSection";
import ChallengeDetailHeader from "../organisms/ChallengeDetailHeader";
import ChallengeCommentsSection from "../organisms/ChallengeCommentsSection";

const ChallengeDetailPage = () => {
  const { challengeId } = useParams();
  const [challenge, setChallenge] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(true);
  const [user, setUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(true);

  // State for handling loading and error
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const theme = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  console.error("error: ", error);

  const dateAndDetailsContent = [
    `Start Date: ${new Date(challenge?.startDate).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`,
    `End Date: ${new Date(challenge?.endDate).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`,
    `Fulfillment Date: ${
      challenge?.fulfillmentDate
        ? new Date(challenge?.fulfillmentDate).toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "Not yet set"
    }`,
    `Winner's Prize: ${challenge?.prize}`,
  ];

  // Check for allowBuys and fanClubOnly conditions and add to the content array
  if (challenge?.advancedSettings?.allowBuys === true) {
    dateAndDetailsContent.push(
      `Other users can support and donate to your submission with a funding goal you set.`
    );
  }

  // Check for chargeSubmissionFee conditions and add to the content array
  if (challenge?.chargeSubmissionFee === true) {
    dateAndDetailsContent.push(
      `This challenge has a per submission fee of $${challenge.submissionFee}.`
    );
  }

  // Check for fanClubOnly condition and add to the content array
  if (challenge?.advancedSettings?.fanClubOnly === true) {
    dateAndDetailsContent.push("This challenge is for Fan Club Members only.");
  }

  useEffect(() => {
    const loadChallenge = async () => {
      try {
        const challengeData = await fetchChallenge(challengeId); // Fetch the challenge data
        setChallenge(challengeData);

        // Fetch the submissions related to this challenge
        const { submissions } = await fetchSubmissions(challengeId, {
          limitNumber: 3,
          sortByDate: "latest",
        });

        setSubmissions(submissions); // Set the submissions data
        setSubmissionsLoading(false); // Set the loading state to false
      } catch (error) {
        console.error(error);
        setSubmissionsLoading(false);
      }
    };

    loadChallenge();
  }, [challengeId]);

  useEffect(() => {
    // Listen for comments on this challenge
    const unsubscribe = fetchComments(
      challengeId,
      setComments,
      setLoadingComments
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [challengeId]);

  useEffect(() => {
    // Check authentication state
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const handleCommentSubmit = async () => {
    if (!user) {
      setSnackbarMessage("You need to be logged in to comment.");
      setSnackbarOpen(true);
      return;
    }

    if (!newComment.trim()) return;

    try {
      await addComment(challengeId, newComment, user);
      setNewComment("");
    } catch (error) {
      console.error("Error adding comment:", error);
      setSnackbarMessage("Failed to add comment. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const handleCreateSubmission = async () => {
    if (!currentUser) {
      // Redirect to sign-in page and preserve the current location
      navigate("/challenge/signin", { state: { from: location } });
      return;
    }

    setIsCreating(true);
    setError(null);

    // console.error('error: ', error);

    try {
      const submissionsCollection = collection(db, "submissions");
      const submissionDoc = await addDoc(submissionsCollection, {
        userId: currentUser.uid,
        challengeId: challengeId,
        title: "", // Initialize with empty or default values
        description: "",
        fundingGoal: 0,
        tags: [],
        status: "draft",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        paymentData: {
          status: "INCOMPLETE",
        },
      });

      const submissionId = submissionDoc.id;

      // Navigate to the submission page with the new submissionId
      navigate(`/challenge/${challengeId}/submission/${submissionId}`);
    } catch (err) {
      console.error("Error creating submission:", err);
      setError("Failed to create submission. Please try again.");
      setSnackbarMessage("Failed to create submission. Please try again.");
      setSnackbarOpen(true);
    } finally {
      setIsCreating(false);
    }
  };

  // Determine if the challenge has ended
  const hasEnded = challenge ? new Date(challenge.endDate) < new Date() : false;

  if (!challenge)
    return (
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1, // Ensures it's above other elements
          backgroundColor: "rgba(0, 0, 0, 0.5)", // Slightly opaque black
        }}
        open={true} // Always open when in this loading state
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );

  return (
    <Container sx={{ mt: 4 }}>
      {/* First Section */}
      <ChallengeDetailHeader
        challenge={challenge}
        challengeId={challengeId}
        user={user}
        navigate={navigate} // Pass navigate function
        theme={theme}
        handleCreateSubmission={handleCreateSubmission} // Pass handler
        isCreating={isCreating} // Pass creating state
        hasEnded={hasEnded} // Pass hasEnded state
      />

      {/* Second Section: Description */}
      <InfoSection
        label="Description"
        content={challenge.description}
        color={theme.colors.green}
        theme
      />

      {/* Third Section: Rules (Only if rules exist) */}
      {challenge.rules && challenge.rules.length > 0 && (
        <InfoSection
          label="Rules"
          content={challenge.rules}
          color="#F9112C"
          bgColor="rgba(134, 138, 156, 0.15)"
        />
      )}

      {/* Fourth Section: Dates and Details */}
      <InfoSection
        label="Submission Details"
        content={dateAndDetailsContent}
        color={theme.colors.amber}
        theme
      />

      {/* Submissions Section */}
      <Box sx={{ mt: 4 }}>
        {/* Conditionally render the SubmissionsSection if there are submissions */}
        {submissionsLoading ? (
          <CircularProgress
            sx={{
              color: theme.colors.red,
            }}
          />
        ) : submissions.length > 0 ? (
          <SubmissionsSection
            header={`${challenge.title}'s Latest Submissions`}
            submissions={submissions}
            link={`/challenge/submissions/all/${challengeId}`}
            theme={theme}
          />
        ) : (
          <Box sx={{ mt: 8, mb: 8 }}>
            <Typography align="center">
              There are no submissions yet.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Comments Section */}
      <ChallengeCommentsSection
        comments={comments}
        loadingComments={loadingComments}
        newComment={newComment}
        setNewComment={setNewComment}
        handleCommentSubmit={handleCommentSubmit}
        snackbarOpen={snackbarOpen}
        setSnackbarOpen={setSnackbarOpen}
        snackbarMessage={snackbarMessage}
        theme={theme}
      />
    </Container>
  );
};

export default ChallengeDetailPage;
